import { useWeb3React } from "@web3-react/core";

import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { FaTimes } from "react-icons/fa";

import { ReactComponent as VerifiedBadge } from "../../assets/images/launchpad/verified-badge.svg";
import { ReactComponent as VerifiedChecker } from "../../assets/images/launchpad/verified-check.svg";

import ERC20_ABI from "../../abis/ERC20.json";

import ABI_LOCKER_CONTRACT from "../../abis/LiquidityLockerDappABI.json";
import LOCKER_TOKEN from "../../abis/TokenLockerabi.json";
import ABI_StorageLaunchpad from "../../abis/ABI_StorageLaunchpad.json";

import {
    DEAD_ADDRESS,
    getPartnerLogo,
    ZERO_ADDRESS,
} from "../../utils/constants";
import { numberConverter, percentageConverter } from "../../utils/utils";
import { getPartnerDetails } from "../../utils/apis";
import { useLockerAddress, useStorageContractAddress } from "../../hooks";

const TokenDistributionChart = ({
    offChainData,
    presaleAddress,
    erc20Address,
    presaleContract,
}) => {
    const STORAGE_CONTRACT_ADDRESS = useStorageContractAddress();
    const LOCKER_ADDRESS = useLockerAddress();
    const { active } = useWeb3React();

    const [totalSupply, setTotalSupply] = useState(0);
    const [chartValues, setChartValues] = useState([]);
    const [projectStore, setProjectStore] = useState({});
    const [partnerKYCDetails, setPartnerKYCDetails] = useState({});
    const [partnerAuditDetails, setPartnerAuditDetails] = useState({});

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const loadData = async () => {
        const web3 = window.web3;

        const tokenContract = new web3.eth.Contract(
            ERC20_ABI.abi,
            erc20Address
        );
        let _decimals = await tokenContract.methods.decimals().call();
        console.log(_decimals);
        const _lockerContract = new web3.eth.Contract(
            ABI_LOCKER_CONTRACT,
            LOCKER_ADDRESS
        );

        const getAllLockers = await _lockerContract.methods
            .getAllLockers()
            .call();

        let _lockedAmount = 0;
        console.log("getAllLockers", getAllLockers);
        for (let lockerAddress of getAllLockers) {
            const _lockerTokenContract = new web3.eth.Contract(
                LOCKER_TOKEN.abi,
                lockerAddress
            );
            _lockerTokenContract.methods
                .lockedToken()
                .call()
                // eslint-disable-next-line no-loop-func
                .then(async (token) => {
                    if (token.toLowerCase() === erc20Address.toLowerCase()) {
                        const _balance = await _lockerTokenContract.methods
                            .LockedBalance()
                            .call();
                        _lockedAmount = _balance / 10 ** _decimals;
                        console.log(_balance, _lockedAmount);
                    }
                });
        }

        let burnedAmount = parseInt(
            web3.utils.fromWei(
                (
                    await tokenContract.methods.balanceOf(DEAD_ADDRESS).call()
                ).toString(),
                "ether"
            )
        );
        burnedAmount += parseInt(
            web3.utils.fromWei(
                (
                    await tokenContract.methods.balanceOf(ZERO_ADDRESS).call()
                ).toString(),
                "ether"
            )
        );

        let presaleAmount =
            (await presaleContract.methods.tokensForPresale().call()) /
            10 ** _decimals;
        let _totalSupply =
            (await tokenContract.methods.totalSupply().call()) /
            10 ** _decimals;
        // console.log(_totalSupply);

        setTotalSupply(_totalSupply);

        let _listingAmount = parseInt(
            (await presaleContract.methods.tokensForLiquidity().call()) /
                10 ** _decimals
        );

        let _chartValues = [0, 0, 0, 0, 0];
        _chartValues[0] = burnedAmount;
        _chartValues[1] = presaleAmount;
        _chartValues[2] = _listingAmount;
        _chartValues[3] = _lockedAmount;

        _chartValues[4] =
            _totalSupply - _chartValues.reduce((pv, cv) => pv + cv, 0);
        setChartValues(_chartValues);

        const _storageContract = new web3.eth.Contract(
            ABI_StorageLaunchpad,
            STORAGE_CONTRACT_ADDRESS
        );
        const _projectStore = await _storageContract.methods
            .ProjectStoreByOwner(presaleAddress)
            .call();
        setProjectStore(_projectStore);
        const _partnerKYCDetails = await getPartnerDetails(
            _projectStore.kycComment
        );
        setPartnerKYCDetails(_partnerKYCDetails);

        const _partnerAuditDetails = await getPartnerDetails(
            _projectStore.auditComment
        );
        setPartnerAuditDetails(_partnerAuditDetails);
        // console.log(_partnerKYCDetails);
    };

    const chartOptions = {
        chart: {
            type: "polarArea",
        },
        yaxis: {
            show: false,
        },
        colors: ["#e20055", "#bc1e5e", "#952b5e", "#6f3056", "#4c2f46"],
        plotOptions: {
            polarArea: {
                rings: {
                    strokeWidth: 0,
                },
                spokes: {
                    strokeWidth: 1,
                },
            },
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            show: false,
        },
        legend: {
            show: false,
        },
        labels: ["Burned", "Presale", "Listing", "Locked", "Unlocked"],
        // theme: {
        //     monochrome: {
        //         enabled: true,
        //         shadeTo: "light",
        //         shadeIntensity: 0.6,
        //     },
        // },
    };

    return (
        <div className="flex flex-row relative items-center mt-4">
            <div className="relative w-8/12 md:w-full -translate-x-[15%] cursor-pointer apexcharts">
                <Chart
                    options={chartOptions}
                    series={chartValues}
                    type="polarArea"
                />
                <span className="hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center size-sm">
                    Total Supply
                    <br />
                    {numberConverter(totalSupply)}
                </span>
            </div>
            <div className="w-fit md:w-full flex flex-col items-center size-sm md:size-base -ml-4">
                <div className="flex flex-col justify-around gap-y-3 pr-1">
                    <span className="donut-label label-burned">
                        Burned{" "}
                        {percentageConverter(chartValues[0], totalSupply)}
                    </span>
                    <span className="donut-label label-presale">
                        Presale{" "}
                        {percentageConverter(chartValues[1], totalSupply)}
                    </span>
                    <span className="donut-label label-listing">
                        Listing{" "}
                        {percentageConverter(chartValues[2], totalSupply)}
                    </span>
                    <span className="donut-label label-unlocked">
                        Locked{" "}
                        {percentageConverter(chartValues[3], totalSupply)}
                    </span>
                    <span className="donut-label label-locked">
                        Unlocked{" "}
                        {percentageConverter(chartValues[4], totalSupply)}
                    </span>
                </div>
                <div className="hidden flex-row pt-2 gap-x-2 w-full justify-center mb-8 md:mb-0">
                    <div
                        className="rounded-lg bg-[#C4C4C422] hover:bg-[#C4C4C444] duration-150 p-1 flex flex-row gap-x-1 items-center cursor-pointer"
                        onClick={() => {
                            if (projectStore.isKYC)
                                window.open(projectStore.isKYCLink, "_black");
                            else if (offChainData.kycLink)
                                window.open(offChainData.kycLink, "_black");
                        }}
                    >
                        {projectStore.isKYC ? (
                            <>
                                <img
                                    src={getPartnerLogo(partnerKYCDetails)}
                                    alt="KYC partner logo"
                                    className="w-8 h-8"
                                />
                                <span className="text-center size-xs">
                                    KYC
                                    <br />
                                    Certificate
                                </span>
                            </>
                        ) : offChainData.kycLink ? (
                            <>
                                <VerifiedBadge className="w-6 ml-1" />
                                <span className="text-center size-xs">
                                    KYC
                                    <br />
                                    Certificate
                                </span>
                            </>
                        ) : (
                            <>
                                <FaTimes className="size-base" />
                                <span className="text-center size-xs">
                                    No KYC
                                    <br />
                                    Certificate
                                </span>
                            </>
                        )}
                    </div>
                    <div
                        className="rounded-lg bg-[#C4C4C422] hover:bg-[#C4C4C444] duration-150 p-1 flex flex-row gap-x-1 items-center cursor-pointer mr-8"
                        onClick={() => {
                            if (projectStore.isAudited)
                                window.open(projectStore.isAuditLink, "_black");
                            else if (offChainData.auditLink)
                                window.open(offChainData.auditLink, "_black");
                        }}
                    >
                        {projectStore.isAudited ? (
                            <>
                                <img
                                    src={getPartnerLogo(partnerAuditDetails)}
                                    alt="Audit partner logo"
                                    className="w-8 h-8"
                                />
                                <span className="text-center size-xs">
                                    Audit
                                    <br />
                                    Certificate
                                </span>
                            </>
                        ) : offChainData.auditLink ? (
                            <>
                                <VerifiedChecker className="w-6 ml-1" />
                                <span className="text-center size-xs">
                                    Audit
                                    <br />
                                    Certificate
                                </span>
                            </>
                        ) : (
                            <>
                                <FaTimes className="size-base" />
                                <span className="text-center size-xs">
                                    No Audit
                                    <br />
                                    Certificate
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TokenDistributionChart;
